import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const verifyAndUpdateSubscription = async () => {
      console.log('✅ [CheckoutSuccess] Starting subscription verification...');
      
      // Get session_id from URL
      const sessionId = searchParams.get('session_id');
      console.log('📋 [CheckoutSuccess] Session ID:', sessionId);
      
      if (sessionId) {
        try {
          // Call backend to verify and update subscription
          const token = await getToken();
          const response = await fetch(`${API_URL}/api/billing/verify-session`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ sessionId })
          });
          
          const data = await response.json();
          console.log('📦 [CheckoutSuccess] Verification response:', data);
          
          if (data.success) {
            console.log('✅ [CheckoutSuccess] Subscription verified and updated!');
            setVerified(true);
          }
        } catch (error) {
          console.error('❌ [CheckoutSuccess] Verification error:', error);
        }
      }
      
      setVerifying(false);
      
      // Trigger subscription update event for UserMenu to refresh
      console.log('📢 [CheckoutSuccess] Dispatching subscription-updated event');
      window.dispatchEvent(new Event('subscription-updated'));
      
      // Auto-redirect after 5 seconds
      const timer = setTimeout(() => {
        navigate('/dashboard');
      }, 5000);

      return () => clearTimeout(timer);
    };
    
    verifyAndUpdateSubscription();
  }, [navigate, searchParams, getToken]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0e1a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)',
        border: '2px solid rgba(16, 185, 129, 0.3)',
        borderRadius: '24px',
        padding: '60px 80px',
        textAlign: 'center',
        maxWidth: '600px',
        boxShadow: '0 20px 60px rgba(16, 185, 129, 0.2)'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: '#10b981',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 30px',
          animation: 'scaleIn 0.5s ease-out'
        }}>
          <CheckCircle size={48} style={{ color: 'white' }} />
        </div>

        {verifying ? (
          <>
            <Loader size={48} style={{ color: 'white', animation: 'spin 1s linear infinite', marginBottom: '20px' }} />
            <h1 style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: 'white',
              marginBottom: '20px'
            }}>
              Verifying Payment...
            </h1>
            <p style={{
              fontSize: '1.125rem',
              color: 'rgba(255,255,255,0.7)',
              lineHeight: '1.6',
              marginBottom: '30px'
            }}>
              Please wait while we activate your subscription
            </p>
          </>
        ) : (
          <>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              color: 'white',
              marginBottom: '20px'
            }}>
              Payment Successful! 🎉
            </h1>

            <p style={{
              fontSize: '1.125rem',
              color: 'rgba(255,255,255,0.7)',
              lineHeight: '1.6',
              marginBottom: '30px'
            }}>
              Your subscription has been {verified ? 'activated' : 'processed'} successfully. Welcome to your new plan!
            </p>
          </>
        )}

        <div style={{
          background: 'rgba(16, 185, 129, 0.2)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <p style={{
            fontSize: '0.95rem',
            color: 'rgba(255,255,255,0.6)',
            margin: 0
          }}>
            You'll receive a confirmation email shortly with your invoice and subscription details.
          </p>
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '14px 32px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
          }}
        >
          Go to Dashboard
        </button>

        <p style={{
          fontSize: '0.875rem',
          color: 'rgba(255,255,255,0.5)',
          marginTop: '20px'
        }}>
          Redirecting automatically in 5 seconds...
        </p>

        <style>{`
          @keyframes scaleIn {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.1);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

